/**
 * Where a page lives, for a language.
 *
 * ⚠️ IT IS ITS OWN MODULE BECAUSE TWO LAYERS NEED IT AND ONE OF THEM IS A BLOCK. `html.tsx` builds
 * the canonical URL, the `hreflang` alternates and the sitemap from it; the navbar's language pill
 * builds the link a visitor presses. Leaving it in `html.tsx` would have made `context.tsx` import
 * the document shell that imports the renderer that imports the context — a cycle, for one function
 * that knows nothing about either.
 */

import type { Lang, Page, Site } from '@ksa/model'

/**
 * The URL path a page is published at, for a language. The default language also lives at the root,
 * so `/` and `/ar/` are the two front doors and neither is a redirect.
 */
export function pathFor(site: Site, page: Page, lang: Lang, index: number): string {
  const prefix = lang === site.defaultLang ? '' : `/${lang}`
  return index === 0 ? `${prefix}/` : `${prefix}/${page.slug}`
}

/** The other language. There are two, and there is no third — see `Text` in `@ksa/model`. */
export function otherLang(lang: Lang): Lang {
  return lang === 'ar' ? 'en' : 'ar'
}

/**
 * Whether this page has anything at all to read in `lang`.
 *
 * ⚠️ THIS EXISTS SO THE LANGUAGE PILL CANNOT SEND SOMEBODY TO A BLANK PAGE. A stranger's generated
 * site is written in ONE language on purpose (§5.15) and the other is filled in later — so on the
 * site as it first exists, half the pages genuinely have nothing in the other language. A switcher
 * that linked there regardless would be the product advertising a page it knows is empty.
 *
 * ⚠️ IT ASKS ABOUT THE PAGE, NOT THE SITE. A site can be half-translated in either direction, and
 * "some of this site is in English" is not a reason to offer English on the page that is not.
 */
export function hasCopyIn(page: Page, lang: Lang): boolean {
  for (const block of page.blocks) {
    if (anyText(block.props, lang)) return true
  }
  return false
}

/**
 * ⚠️ IT WALKS ARRAYS AND NESTED OBJECTS, because a repeater's rows are where half a page's copy
 * lives — a `contact` block's opening hours or a `features` block's list. A shallow pass over the
 * block's own props would call a page of translated features "empty".
 */
function anyText(value: unknown, lang: Lang): boolean {
  if (!value || typeof value !== 'object') return false
  if (Array.isArray(value)) return value.some((item) => anyText(item, lang))

  const record = value as Record<string, unknown>
  // A `{ lit }` string is the same in both languages by definition — a phone number, a brand name —
  // so it is deliberately NOT evidence that this page reads in `lang`.
  if (typeof record['lit'] === 'string') return false
  if (typeof record[lang] === 'string') return record[lang].trim().length > 0

  return Object.values(record).some((item) => anyText(item, lang))
}
